import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import axios, { AxiosResponse } from "axios";
import { DataTableWithoutButtons } from "components/DataTable/DataTableWithoutButtons";
import { AfDataType } from "utils/TableColumnData";
import handleWheel from "utils/handleOnWheel";
import { DeleteCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { UseMutateFunction, useMutation, useQuery } from "@tanstack/react-query";
import { Img } from "components";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import { roles } from "utils/constant";
import { utils, write, writeFile } from "xlsx";
import { saveAs } from "file-saver";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon, DocumentDuplicateIcon, PrinterIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Button, Checkbox } from "@heroui/react";

const PaymentStatement = forwardRef<any, any>(
  ({ customerId, customerData, searchIncrement, selectedInvoice: propSelectedInvoice, setSelectedInvoice: propSetSelectedInvoice, setInvoiceTableData, tableRef }, ref) => {
  const ADT = AfDataType;

  const [internalSelectedInvoice, setInternalSelectedInvoice] = useState<any[]>([]);
  const selectedInvoice = propSelectedInvoice !== undefined ? propSelectedInvoice : internalSelectedInvoice;
  const setSelectedInvoice = propSetSelectedInvoice || setInternalSelectedInvoice;

  const [tableData, setTableData] = useState<any>([]);
  const [tableDetails, setTableDetails] = useState<any>([]);
  const [rawInvoices, setRawInvoices] = useState<any[]>([]);
  const [rawDebitNotes, setRawDebitNotes] = useState<any[]>([]);
  const [rawCreditNotes, setRawCreditNotes] = useState<any[]>([]);

  const [startDate, setStartDate] = useState<any>(moment().startOf("day").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState<any>(moment().endOf("day").format("YYYY-MM-DD"));
  const [query, setQuery] = useState<any>({
    fromDate: moment().startOf("day"),
    toDate: moment().endOf("day"),
  });
  const [isDataAdded, setIsDataAdded] = useState(false);

  const [allAgents, setAllAgents] = useState([]);

  const [totalCredit, setTotalCredit] = useState(0);
  const [totalTdsCredit, setTotalTdsCredit] = useState(0);
  const [pendingAmount, setPendingAmount] = useState(0);

  const [invoiceWisePayment, setInvoiceWisePayment] = useState({});
  const [selectedSum, setSelectedSum] = useState<number | null>(null);
  const [showSum, setShowSum] = useState(false);
  const [selectedCellsSet, setSelectedCellsSet] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (searchIncrement > 0 && customerId) {
      //console.log("Search Increment Changed:", searchIncrement, customerId);
      setIsDataAdded(!isDataAdded);
      setQuery({
        /*fromDate: startDate ? moment(startDate).startOf("day") : moment().startOf("day"),
        toDate: endDate ? moment(endDate).endOf("day") : moment().endOf("day"),*/
        customer: customerId,
      });
    }
  }, [searchIncrement]);

  const { isFetching, isLoading, refetch } = useQuery(
    ["admin_statementp_report", query.customer, searchIncrement],
    () => ListCommanWay("/admin/collection/list", { customer: query.customer }, { include: ["_addedBy"], paginate: 10000 }),
    {
      keepPreviousData: false,
      enabled: !!query.customer && searchIncrement > 0,
      onSuccess: async (paymentResponse) => {
        if (paymentResponse.status !== "SUCCESS") {
          setTableData([]);
          setInvoiceWisePayment({});
          return;
        }

        try {
          const invoiceResponse = await ListCommanWay(
            "/admin/inovic_for_c_b/list",
            { customerId: query.customer, fromDate: { $gte: "2024-04-01" } },
            { include: ["_subbranch"], paginate: 10000 }
          );

          if (invoiceResponse.status !== "SUCCESS") {
            setTableData([]);
            return;
          }

          // Fetch Debit Notes and Credit Notes for this customer
          let debitNotes: any[] = [];
          let creditNotes: any[] = [];
          try {
            const cnDnResponse = await ListCommanWay(
              "/admin/cn-dn/list",
              { customerId: query.customer },
              { include: ["_customerId", "_addedBy", "_subbranch"], paginate: 10000 }
            );

            if (cnDnResponse.status === "SUCCESS") {
              const rawNotes = Array.isArray(cnDnResponse.data?.data)
                ? cnDnResponse.data.data
                : Array.isArray(cnDnResponse.data)
                ? cnDnResponse.data
                : [];

              const isValidIrnStatus = (status: any) => {
                const s = String(status || "").trim().toUpperCase();
                return s === "ACTIVE" || s === "NA" || s === "N/A";
              };
              const activeNotes = rawNotes.filter((n: any) => isValidIrnStatus(n.irn_status));
              debitNotes = activeNotes.filter((n: any) => n.note_type === "DN");
              creditNotes = activeNotes.filter((n: any) => n.note_type === "CN");
            }
          } catch (cnErr) {
            console.error("Error fetching CN/DN:", cnErr);
          }

          setRawDebitNotes(debitNotes);
          setRawCreditNotes(creditNotes);

          const payments = paymentResponse.data.data;
          const invoices = invoiceResponse.data.data;
          setRawInvoices(invoices);
          setInvoiceTableData(invoices);

          // Helper to extract linked invoice IDs from a note
          const getNoteInvoiceIds = (note: any): number[] => {
            let ids: any[] = [];
            if (Array.isArray(note.invoice_ids)) {
              ids = ids.concat(note.invoice_ids);
            }
            if (Array.isArray(note.linked_invoices)) {
              ids = ids.concat(note.linked_invoices.map((x: any) => (typeof x === "object" ? x?.id : x)));
            }
            return ids.map((id) => Number(id)).filter((id) => !isNaN(id) && id > 0);
          };

          // Step 1: Build invoice map
          const invoiceMap: any = {};

          invoices.forEach((inv: any) => {
            const invId = inv.id;
            const totalVal = Number(inv.total_value || 0);
            const baseVal =
              Number(inv.base_value || 0) ||
              (Number(inv.total_d_tax || 0) + Number(inv.total_i_tax || 0)) ||
              Math.round(totalVal / 1.18);

            const branchCode = inv._subbranch?.branch_code || "";
            const isInternational = inv.invoice_type === "INTERNATIONAL";
            const invNoFormatted = `${branchCode}${isInternational ? "I" : ""}${inv.invoice_no}`;

            invoiceMap[invId] = {
              invoiceId: invId,
              invoiceNo: invNoFormatted,
              invoiceAmount: totalVal,
              baseAmount: baseVal,
              allocated: 0,
              allocatedTds: 0,
              pending: totalVal,
              invDate: inv.invDate,
              payments: [],
              creditNotes: [],
              debitNotes: [],
            };
          });

          // Step 2: Allocate linked Debit Notes to invoices (increases invoice pending/total)
          debitNotes.forEach((dn: any) => {
            const dnAmount = Number(dn.total_value || 0);
            const linkedIds = getNoteInvoiceIds(dn);
            linkedIds.forEach((invId: any) => {
              if (invoiceMap[invId]) {
                invoiceMap[invId].invoiceAmount += dnAmount;
                invoiceMap[invId].pending += dnAmount;
                invoiceMap[invId].debitNotes.push({
                  noteId: dn.id,
                  noteNo: dn.note_no,
                  amount: dnAmount,
                  date: dn.note_date || dn.createdAt,
                  reason: dn.reason,
                });
              }
            });
          });

          // Step 3: Process payments and allocate to linked invoices
          let totalReceived = 0;
          let totalPaymentTds = 0;

          payments.forEach((payment: any) => {
            const paymentAmount = Number(payment.totalAmount || 0);
            const basicAmount = Number(payment.basicAmount || 0);
            const tdsPercent = Number(payment.tds || 0);

            const tdsAmount = Math.round(basicAmount * (tdsPercent / 100)); // e.g., 900149 * 2 / 100 = 18003
            const totalEffectiveAmount = paymentAmount + tdsAmount; // Total that covers invoice(s)

            const linkedInvoices: number[] = payment.invoiceJson?.invoices || [];
            const paymentDate = payment.payRecvDate;
            const paymentId = payment.id;
            const remark = payment.remark || "";

            totalReceived += paymentAmount;
            totalPaymentTds += tdsAmount;

            if (linkedInvoices.length === 0) {
              // On-account payment (not linked)
              return;
            }

            // Calculate positive pending across linked invoices to determine allocation ratio
            const positivePendingForThisPayment = linkedInvoices.reduce((sum, invId) => {
              const p = invoiceMap[invId]?.pending || 0;
              return sum + (p > 0 ? p : 0);
            }, 0);

            // Allocate proportionally based on each invoice's pending amount
            linkedInvoices.forEach((invId) => {
              const inv = invoiceMap[invId];
              if (!inv) return;

              // Calculate this invoice's share of the payment
              let ratio = 0;
              if (linkedInvoices.length === 1) {
                ratio = 1;
              } else if (positivePendingForThisPayment > 0) {
                ratio = (inv.pending > 0 ? inv.pending : 0) / positivePendingForThisPayment;
              } else {
                ratio = 1 / linkedInvoices.length;
              }

              if (ratio <= 0) return;

              const allocatePayment = Number((paymentAmount * ratio).toFixed(2));
              const allocateTds = Number((tdsAmount * ratio).toFixed(2));
              const allocateTotal = allocatePayment + allocateTds;

              inv.allocated += allocatePayment;
              inv.allocatedTds += allocateTds;
              // Allow negative pending when invoice is deliberately overpaid
              inv.pending = Number((inv.pending - allocateTotal).toFixed(2));

              inv.payments.push({
                paymentId,
                amount: allocatePayment,
                recvdTds: allocateTds,
                date: paymentDate,
                remark,
              });
            });
          });

          // Credit Notes are not deducted from individual invoices (per requirement).
          // They appear directly as CREDIT entries in the statement and reduce the overall Pending Amount.

          // Final invoice-wise allocation
          setInvoiceWisePayment(invoiceMap);

          // Step 6: Build unified ledger table (Payments, Credit Notes, Invoices, Debit Notes)
          let tdata: any[] = [];

          // Add Credits (Payments Received)
          payments.forEach((c: any) => {
            const date = moment(c.payRecvDate, "DD-MM-YYYY HH:mm:ss").toDate();
            tdata.push({
              type: "CREDIT",
              subType: "PAYMENT",
              date,
              amount: Number(c.totalAmount || 0),
              tds: Math.round((c.basicAmount * c.tds) / 100) || 0,
              remark: c.remark || "",
              paymentId: c.id,
              payments: c.invoiceJson?.invoices || [],
              addedBy: c._addedBy?.name || "",
              createdAt: c.createdAt,
            });
          });

          // Add Credits (Credit Notes)
          creditNotes.forEach((cn: any) => {
            const date = cn.note_date ? moment(cn.note_date).toDate() : moment(cn.createdAt).toDate();
            const linkedInvIds = getNoteInvoiceIds(cn);
            tdata.push({
              type: "CREDIT",
              subType: "CN",
              isCreditNote: true,
              date,
              amount: Number(cn.total_value || 0),
              tds: 0,
              remark: cn.reason ? (cn.remarks ? `${cn.reason} - ${cn.remarks}` : cn.reason) : (cn.remarks || "Credit Note"),
              invoiceNo: cn.note_no,
              noteId: cn.id,
              payments: linkedInvIds,
              addedBy: cn._addedBy?.name || "",
              createdAt: cn.createdAt,
            });
          });

          // Add Debits (Invoices)
          invoices.forEach((c: any) => {
            const date = moment(c.invDate).toDate();
            const branchCode = c._subbranch?.branch_code || "";
            const isInternational = c.invoice_type === "INTERNATIONAL";
            const invNoFormatted = `${branchCode}${isInternational ? "I" : ""}${c.invoice_no}`;

            tdata.push({
              type: "DEBIT",
              subType: "INVOICE",
              date,
              amount: Number(c.total_value || 0),
              remark: c.utrNo || "",
              invoiceNo: invNoFormatted,
              invoiceId: c.id,
              fromDate: moment(c.fromDate).format("DD-MM-YYYY"),
              toDate: moment(c.toDate).format("DD-MM-YYYY"),
              followupDate:
                c.payDate && moment(c.payDate, "DD-MM-YYYY HH:mm:ss").isValid()
                  ? moment(c.payDate, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY")
                  : "-",
            });
          });

          // Add Debits (Debit Notes)
          debitNotes.forEach((dn: any) => {
            const date = dn.note_date ? moment(dn.note_date).toDate() : moment(dn.createdAt).toDate();
            tdata.push({
              type: "DEBIT",
              subType: "DN",
              isDebitNote: true,
              date,
              amount: Number(dn.total_value || 0),
              remark: dn.reason ? (dn.remarks ? `${dn.reason} - ${dn.remarks}` : dn.reason) : (dn.remarks || "Debit Note"),
              invoiceNo: dn.note_no,
              noteId: dn.id,
              addedBy: dn._addedBy?.name || "",
              createdAt: dn.createdAt,
              fromDate: "-",
              toDate: "-",
              followupDate: "-",
            });
          });

          // Sort by date
          tdata.sort((a, b) => {
            const timeDiff = a.date.getTime() - b.date.getTime();
            if (timeDiff !== 0) return timeDiff;
            return a.type === "DEBIT" ? -1 : 1;
          });

          // Calculate running balance and assign status
          // DEBIT increases receivable, CREDIT decreases receivable
          let balance = 0;
          let tdsCredit = 0;
          tdata = tdata.map((item) => {
            if (item.type === "CREDIT") {
              balance -= item.amount + (item.tds || 0);
            } else {
              balance += item.amount;
              if (item.invoiceId && invoiceMap[item.invoiceId]) {
                const inv = invoiceMap[item.invoiceId];
                item.allocatedAmount = inv.allocated;
                item.effRcvd = inv.payments.reduce((sum: number, p: any) => sum + (p.amount || 0) + (p.recvdTds || 0), 0);
                tdsCredit += inv.payments.reduce((sum: number, p: any) => sum + (p.recvdTds || 0), 0);
                item.outstanding = inv.pending;
                item.status = inv.pending < -10 ? "Overpaid" : inv.pending < 10 ? "Cleared" : "Pending";
              } else if (item.isDebitNote) {
                item.allocatedAmount = 0;
                item.effRcvd = 0;
                item.outstanding = item.amount;
                item.status = Number(item.amount || 0) < 10 ? "Cleared" : "Pending";
              }
            }
            item.runningBalance = balance;
            return item;
          });

          const totalInvoiceAmount = invoices.reduce((sum: number, inv: any) => sum + Number(inv.total_value || 0), 0);
          const totalDns = debitNotes.reduce((sum: number, dn: any) => sum + Number(dn.total_value || 0), 0);
          const totalCns = creditNotes.reduce((sum: number, cn: any) => sum + Number(cn.total_value || 0), 0);

          const grandBilled = totalInvoiceAmount + totalDns;
          const grandCredit = totalReceived + totalCns;
          const finalTds = Math.max(tdsCredit, totalPaymentTds);
          const finalPending = grandBilled - grandCredit - finalTds;

          setPendingAmount(finalPending);
          setTotalCredit(grandCredit);
          setTotalTdsCredit(finalTds);
          console.log(tdata);
          setTableData(tdata);

          console.log("Invoice Wise Payment Allocation:", invoiceMap);
        } catch (err) {
          console.error("Error fetching statement data:", err);
          setTableData([]);
        }
      },
      onError: () => {
        setTableData([]);
        setInvoiceWisePayment({});
      },
    }
  );

  // Delete collection/payment mutation
  const { mutate: deleteCollection, isLoading: isDeleting } = useMutation(
    (paymentId: number) => DeleteCommanWay({ url: `/admin/collection/delete/${paymentId}`, data: {} }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("Payment deleted successfully!", "success");
          refetch();
        } else {
          UseToast(data.message || "Failed to delete payment", "error");
        }
      },
      onError: () => {
        UseToast("Failed to delete payment", "error");
      },
    }
  );

  const handleDeletePayment = (paymentId: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteCollection(paymentId);
      }
    });
  };

  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const userD = (() => { const auth = localStorage.getItem("auth"); return auth ? JSON.parse(auth) : {}; })();
  const [userDetails, setUserDetails] = useState<any>(userD?.state?.user || {});

  const handleMouseUp = () => {
    const selection = window.getSelection();

    // Clear previous selection highlighting
    const table = tableRef.current;
    if (table) {
      const allCells = (table as HTMLElement).querySelectorAll("td");
      allCells.forEach((cell) => cell.classList.remove("cell-selected"));
    }

    if (!selection || selection.toString().trim() === "") {
      setShowSum(false);
      setSelectedCellsSet(new Set());
      return;
    }

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;

    // Check if selection is within the table
    let node: any = container;
    let isInTable = false;
    while (node) {
      if (node === table) {
        isInTable = true;
        break;
      }
      node = node.parentNode;
    }

    if (!isInTable) {
      setShowSum(false);
      setSelectedCellsSet(new Set());
      return;
    }

    // Get all selected cells
    const selectedCells: HTMLElement[] = [];

    // Find start and end TD cells
    let startNode: any = range.startContainer;
    while (startNode && startNode.tagName !== "TD" && startNode !== table) {
      startNode = startNode.parentElement;
    }

    let endNode: any = range.endContainer;
    while (endNode && endNode.tagName !== "TD" && endNode !== table) {
      endNode = endNode.parentElement;
    }

    if (!startNode || !endNode || startNode.tagName !== "TD" || endNode.tagName !== "TD") {
      setShowSum(false);
      return;
    }

    // Get row and column indices for start and end cells
    const startRow = startNode.parentElement;
    const endRow = endNode.parentElement;
    const startColIndex = Array.from(startRow.children).indexOf(startNode);
    const endColIndex = Array.from(endRow.children).indexOf(endNode);

    // Get all rows in tbody
    const tbody = (table as HTMLElement).querySelector("tbody");
    if (!tbody) return;

    const allRows = Array.from(tbody.querySelectorAll("tr")) as HTMLElement[];
    const startRowIndex = allRows.indexOf(startRow);
    const endRowIndex = allRows.indexOf(endRow);

    const minRow = Math.min(startRowIndex, endRowIndex);
    const maxRow = Math.max(startRowIndex, endRowIndex);
    const minCol = Math.min(startColIndex, endColIndex);
    const maxCol = Math.max(startColIndex, endColIndex);

    // Select cells in the rectangular range
    for (let rowIdx = minRow; rowIdx <= maxRow; rowIdx++) {
      const row = allRows[rowIdx];
      const cells = Array.from(row.children) as HTMLElement[];

      for (let colIdx = minCol; colIdx <= maxCol; colIdx++) {
        const cell = cells[colIdx];
        if (cell && cell.tagName === "TD") {
          // Only include amount columns: 6=DEBIT, 7=CREDIT, 8=Balance, 9=TDS, 10=Eff.Rcvd, 11=Outstanding
          if ([6, 7, 8, 9, 10, 11].includes(colIdx)) {
            selectedCells.push(cell);
          }
        }
      }
    }

    // Extract numeric values from amount-related columns and apply highlighting
    let sum = 0;
    let hasValidNumbers = false;

    selectedCells.forEach((cell) => {
      cell.classList.add("cell-selected");
      const text = cell.textContent?.trim() || "";
      // Remove commas and parse
      const numText = text.replace(/,/g, "");
      const num = parseFloat(numText);
      if (!isNaN(num)) {
        sum += num;
        hasValidNumbers = true;
      }
    });

    if (hasValidNumbers && selectedCells.length > 1) {
      setSelectedSum(sum);
      setShowSum(true);
    } else {
      setShowSum(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousedown", () => {
      // Clear selection on new mouse down
      const table = tableRef.current;
      if (table) {
        const allCells = (table as HTMLElement).querySelectorAll("td");
        allCells.forEach((cell) => cell.classList.remove("cell-selected"));
      }
    });

    // Inject styles directly into head
    const styleId = "payment-statement-selection-style";
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = `
      #myTable tbody td:nth-child(7),
      #myTable tbody td:nth-child(8),
      #myTable tbody td:nth-child(9),
      #myTable tbody td:nth-child(10),
      #myTable tbody td:nth-child(11),
      #myTable tbody td:nth-child(12) {
        cursor: cell !important;
        user-select: text !important;
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
      }
      #myTable ::selection {
        background-color: transparent !important;
        color: inherit !important;
      }
      #myTable ::-moz-selection {
        background-color: transparent !important;
        color: inherit !important;
      }
      #myTable tbody td.cell-selected {
        background-color: #3b82f6 !important;
        color: white !important;
      }
    `;

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);

  const numberToWordsIndian = (num: number): string => {
    if (!num || isNaN(num)) return "Zero";
    const a = [
      "", "One ", "Two ", "Three ", "Four ", "Five ", "Six ", "Seven ", "Eight ", "Nine ", "Ten ",
      "Eleven ", "Twelve ", "Thirteen ", "Fourteen ", "Fifteen ", "Sixteen ", "Seventeen ", "Eighteen ", "Nineteen "
    ];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const inWords = (n: number): string => {
      let str = "";
      if (n >= 10000000) {
        str += inWords(Math.floor(n / 10000000)) + "Crore ";
        n %= 10000000;
      }
      if (n >= 100000) {
        str += inWords(Math.floor(n / 100000)) + "Lakh ";
        n %= 100000;
      }
      if (n >= 1000) {
        str += inWords(Math.floor(n / 1000)) + "Thousand ";
        n %= 1000;
      }
      if (n >= 100) {
        str += inWords(Math.floor(n / 100)) + "Hundred ";
        n %= 100;
      }
      if (n > 0) {
        if (n < 20) {
          str += a[n];
        } else {
          str += b[Math.floor(n / 10)] + " " + a[n % 10];
        }
      }
      return str;
    };

    const integerPart = Math.floor(Math.abs(num));
    const decimalPart = Math.round((Math.abs(num) - integerPart) * 100);

    let result = "Rupees " + (inWords(integerPart).trim() || "Zero");
    if (decimalPart > 0) {
      result += " and " + inWords(decimalPart).trim() + "Paise";
    }
    result += " Only";
    return result;
  };

  const handleOutstandingSlip = () => {
    if (!customerId) {
      UseToast("Please select a customer first", "error");
      return;
    }
    if (isFetching || isLoading) {
      UseToast("Statement data is loading. Please wait...", "warning");
      return;
    }
    if (!tableData || tableData.length === 0) {
      UseToast("No statement data found for this customer", "warning");
      return;
    }

    const customerName = customerData?.sh || customerData?.name || "Customer";
    const customerCode = customerData?.code || "-";
    const customerGst = customerData?.gst || "-";
    let customerAddress = customerData?.address || "-";
    if (customerAddress !== "-" && customerData?.pincode && !customerAddress.includes(customerData?.pincode)) {
      customerAddress += ` - ${customerData?.pincode}`;
    }
    const customerPhone = customerData?.mobile || customerData?.officePhone || "-";
    const customerEmail = customerData?.officeEmail || "-";
    const companyGst = "24AALCA0600L1Z0";
    const slipDate = moment().format("DD/MM/YYYY");

    const isFilteredBySelection = Array.isArray(selectedInvoice) && selectedInvoice.length > 0;

    // Extract pending items directly from tableData (guarantees 100% match with Statement ledger view)
    const allPendingItems = (tableData || [])
      .filter((r: any) => {
        if (isFilteredBySelection) {
          return r.type === "DEBIT" && r.invoiceId && selectedInvoice.includes(r.invoiceId);
        }
        return (
          r.type === "DEBIT" &&
          (r.subType === "INVOICE" || r.subType === "DN") &&
          Number(r.outstanding || 0) >= 10
        );
      })
      .map((r: any) => {
        const invDate = r.date ? moment(r.date) : null;
        const fromDateStr = r.fromDate && r.fromDate !== "-" ? r.fromDate : "";
        const toDateStr = r.toDate && r.toDate !== "-" ? r.toDate : "";
        const periodStr = fromDateStr && toDateStr ? `${fromDateStr} to ${toDateStr}` : (r.remark || "-");
        const invAmount = Number(r.amount || 0);
        const allocated = Number(r.allocatedAmount || 0);
        const effRcvd = Number(r.effRcvd || 0);
        const recvdTds = Math.max(0, Number((effRcvd - allocated).toFixed(2)));
        const pending = Number(Number(r.outstanding || 0).toFixed(2));
        const invoiceNo = r.subType === "DN" ? `${r.invoiceNo} (DN)` : (r.invoiceNo || "-");

        return {
          invoiceNo,
          invAmount,
          allocated,
          recvdTds,
          pending,
          invDateStr: invDate && invDate.isValid() ? invDate.format("DD/MM/YYYY") : "-",
          rawDate: invDate ? invDate.valueOf() : 0,
          periodStr,
        };
      })
      .sort((a: any, b: any) => a.rawDate - b.rawDate);

    if (isFilteredBySelection && allPendingItems.length === 0) {
      UseToast("Selected invoice(s) not found in statement data", "warning");
      return;
    }

    // Sum totals of pending invoices & notes
    const totalPendingInvAmt = allPendingItems.reduce((sum, r) => sum + r.invAmount, 0);
    const totalPendingAllocated = allPendingItems.reduce((sum, r) => sum + r.allocated, 0);
    const totalPendingTds = allPendingItems.reduce((sum, r) => sum + r.recvdTds, 0);
    const totalPendingOutstanding = Number(allPendingItems.reduce((sum, r) => sum + r.pending, 0).toFixed(2));

    // Closing balance
    const closingBalance = isFilteredBySelection
      ? totalPendingOutstanding
      : Number(pendingAmount.toFixed(2));

    // Credit notes from tableData
    const creditNoteItems = isFilteredBySelection
      ? []
      : (tableData || []).filter((r: any) => r.type === "CREDIT" && (r.subType === "CN" || r.isCreditNote));
    const totalCnAmount = Number(creditNoteItems.reduce((sum: number, r: any) => sum + Number(r.amount || 0), 0).toFixed(2));
    const diff = Number((totalPendingOutstanding - closingBalance).toFixed(2));
    const cnNumbers = creditNoteItems.map((c: any) => c.invoiceNo).filter(Boolean).join(", ");

    // Summary breakdown: write the exact cause of any difference between pending invoices total and closing balance
    let summaryRowsHtml = "";
    if (isFilteredBySelection) {
      summaryRowsHtml = `
        <tr>
          <td class="summary-label">Total Amount of Selected Invoices (${allPendingItems.length}):</td>
          <td class="summary-val text-right">₹ ${totalPendingInvAmt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        ${totalPendingAllocated > 0 ? `
        <tr>
          <td class="summary-label">Less: Amount Paid / Received:</td>
          <td class="summary-val text-right">- ₹ ${totalPendingAllocated.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        ` : ""}
        ${totalPendingTds > 0 ? `
        <tr>
          <td class="summary-label">Less: TDS Deducted:</td>
          <td class="summary-val text-right">- ₹ ${totalPendingTds.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        ` : ""}
        <tr class="net-row">
          <td class="summary-label" style="font-size: 14px;"><strong>Total Outstanding Amount Payable:</strong></td>
          <td class="summary-val text-right" style="font-size: 14px;"><strong>₹ ${totalPendingOutstanding.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
        </tr>
      `;
    } else if (closingBalance < -10) {
      summaryRowsHtml = `
        <tr class="overpaid-row">
          <td class="summary-label" style="color: #15803d;"><strong>Account Status:</strong></td>
          <td class="summary-val text-right" style="color: #15803d;"><strong>OVERPAID (CREDIT BALANCE)</strong></td>
        </tr>
        ${totalPendingOutstanding > 0 ? `
        <tr>
          <td class="summary-label">Total of Pending Invoices & Notes:</td>
          <td class="summary-val text-right">₹ ${totalPendingOutstanding.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        ` : ""}
        <tr class="net-row">
          <td class="summary-label" style="font-size: 14px;"><strong>Closing Balance (Credit / Advance Available):</strong></td>
          <td class="summary-val text-right" style="font-size: 14px; color: #15803d;"><strong>- ₹ ${Math.abs(closingBalance).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Cr)</strong></td>
        </tr>
      `;
    } else if (diff > 10) {
      if (totalCnAmount > 0 && Math.abs(diff - totalCnAmount) <= 10) {
        // Difference is completely due to Credit Notes
        summaryRowsHtml = `
          <tr>
            <td class="summary-label">Total of Pending Invoices & Notes:</td>
            <td class="summary-val text-right">₹ ${totalPendingOutstanding.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr class="overpaid-row" style="color: #15803d;">
            <td class="summary-label">Less: Credit Note(s) ${cnNumbers ? `(${cnNumbers})` : ""}:</td>
            <td class="summary-val text-right">- ₹ ${totalCnAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr class="net-row">
            <td class="summary-label" style="font-size: 14px;"><strong>Final Closing Balance (Net Outstanding):</strong></td>
            <td class="summary-val text-right" style="font-size: 14px;"><strong>₹ ${closingBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
          </tr>
        `;
      } else if (totalCnAmount > 0 && diff > totalCnAmount) {
        // Difference is Credit Notes + On-Account Advance Payment
        const advanceAmount = Number((diff - totalCnAmount).toFixed(2));
        summaryRowsHtml = `
          <tr>
            <td class="summary-label">Total of Pending Invoices & Notes:</td>
            <td class="summary-val text-right">₹ ${totalPendingOutstanding.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr class="overpaid-row" style="color: #15803d;">
            <td class="summary-label">Less: Credit Note(s) ${cnNumbers ? `(${cnNumbers})` : ""}:</td>
            <td class="summary-val text-right">- ₹ ${totalCnAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr class="overpaid-row" style="color: #15803d;">
            <td class="summary-label">Less: On-Account Advance Payment / Credit:</td>
            <td class="summary-val text-right">- ₹ ${advanceAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr class="net-row">
            <td class="summary-label" style="font-size: 14px;"><strong>Final Closing Balance (Net Outstanding):</strong></td>
            <td class="summary-val text-right" style="font-size: 14px;"><strong>₹ ${closingBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
          </tr>
        `;
      } else if (totalCnAmount > 0) {
        // Difference is Credit Notes (partial or adjusted)
        summaryRowsHtml = `
          <tr>
            <td class="summary-label">Total of Pending Invoices & Notes:</td>
            <td class="summary-val text-right">₹ ${totalPendingOutstanding.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr class="overpaid-row" style="color: #15803d;">
            <td class="summary-label">Less: Credit Note(s) ${cnNumbers ? `(${cnNumbers})` : ""}:</td>
            <td class="summary-val text-right">- ₹ ${diff.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr class="net-row">
            <td class="summary-label" style="font-size: 14px;"><strong>Final Closing Balance (Net Outstanding):</strong></td>
            <td class="summary-val text-right" style="font-size: 14px;"><strong>₹ ${closingBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
          </tr>
        `;
      } else {
        // Difference is On-Account Advance Payment / Opening Credit
        summaryRowsHtml = `
          <tr>
            <td class="summary-label">Total of Pending Invoices & Notes:</td>
            <td class="summary-val text-right">₹ ${totalPendingOutstanding.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr class="overpaid-row" style="color: #15803d;">
            <td class="summary-label">Less: On-Account Advance Payment / Credit:</td>
            <td class="summary-val text-right">- ₹ ${diff.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
          <tr class="net-row">
            <td class="summary-label" style="font-size: 14px;"><strong>Final Closing Balance (Net Outstanding):</strong></td>
            <td class="summary-val text-right" style="font-size: 14px;"><strong>₹ ${closingBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
          </tr>
        `;
      }
    } else if (closingBalance > totalPendingOutstanding + 10) {
      const priorDebit = Number((closingBalance - totalPendingOutstanding).toFixed(2));
      summaryRowsHtml = `
        <tr>
          <td class="summary-label">Total of Listed Pending Invoices & Notes:</td>
          <td class="summary-val text-right">₹ ${totalPendingOutstanding.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        <tr>
          <td class="summary-label">Add: Prior Dues / Opening Balance:</td>
          <td class="summary-val text-right">+ ₹ ${priorDebit.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
        <tr class="net-row">
          <td class="summary-label" style="font-size: 14px;"><strong>Final Closing Balance (Net Outstanding):</strong></td>
          <td class="summary-val text-right" style="font-size: 14px;"><strong>₹ ${closingBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
        </tr>
      `;
    } else {
      summaryRowsHtml = `
        <tr class="net-row">
          <td class="summary-label" style="font-size: 14px;"><strong>Final Closing Balance (Net Outstanding):</strong></td>
          <td class="summary-val text-right" style="font-size: 14px;"><strong>₹ ${closingBalance.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></td>
        </tr>
      `;
    }

    const tableRowsHtml = allPendingItems.map((inv: any, idx: number) => {
      return `
        <tr>
          <td class="text-center">${idx + 1}</td>
          <td class="text-center font-bold">${inv.invoiceNo || "-"}</td>
          <td class="text-center">${inv.invDateStr}</td>
          <td class="text-center">${inv.periodStr}</td>
          <td class="text-right">${inv.invAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          <td class="text-right">${inv.allocated.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          <td class="text-right">${inv.recvdTds.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          <td class="text-right font-bold">${inv.pending.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
      `;
    }).join("");

    const words = numberToWordsIndian(Math.abs(closingBalance)) + (closingBalance < 0 ? " (Credit / Advance)" : "");

    const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Outstanding Slip - ${customerName} (${customerCode})</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 25px; color: #1e293b; background: #fff; }
    .container { max-width: 1050px; margin: 0 auto; border: 2px solid #334155; padding: 25px; border-radius: 4px; }
    .header { display: flex; align-items: center; border-bottom: 2px solid #334155; padding-bottom: 12px; margin-bottom: 16px; position: relative; }
    .header img { height: 50px; position: absolute; left: 0; }
    .header .header-text { flex: 1; text-align: center; }
    .header h1 { font-size: 20px; font-weight: bold; color: #0f172a; letter-spacing: 0.5px; }
    .header h2 { font-size: 14px; color: #64748b; margin-top: 3px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; }
    .title { text-align: center; font-size: 16px; font-weight: bold; margin: 12px 0 16px 0; text-decoration: underline; letter-spacing: 1px; color: #0f172a; }
    .info-section { display: flex; justify-content: space-between; margin-bottom: 16px; font-size: 12px; line-height: 1.5; background: #f8fafc; padding: 12px; border: 1px solid #e2e8f0; border-radius: 6px; }
    .info-section .left { width: 65%; }
    .info-section .right { width: 33%; }
    .info-section p { margin: 3px 0; }
    .info-section .label { font-weight: 600; display: inline-block; min-width: 110px; color: #475569; }
    table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 11px; }
    th { background: #f1f5f9; border: 1px solid #334155; padding: 6px 6px; text-align: center; font-weight: 600; color: #0f172a; }
    td { border: 1px solid #334155; padding: 5px 6px; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .font-bold { font-weight: bold; }
    .total-row { font-weight: bold; background: #f8fafc; }
    .summary-box { margin: 16px 0 10px 0; padding: 10px 14px; border: 1.5px solid #334155; border-radius: 4px; background: #f8fafc; max-width: 550px; margin-left: auto; }
    .summary-table { width: 100%; border: none; margin: 0; font-size: 12px; }
    .summary-table td { border: none; padding: 3px 6px; }
    .summary-label { font-weight: 500; color: #334155; }
    .summary-val { font-weight: 600; color: #0f172a; }
    .overpaid-row td { color: #15803d !important; }
    .net-row td { border-top: 1.5px solid #334155 !important; padding-top: 6px !important; }
    .amount-words { font-size: 11.5px; margin: 10px 0 16px 0; font-style: italic; color: #334155; }
    .footer { margin-top: 20px; display: flex; justify-content: space-between; align-items: flex-end; font-size: 11px; }
    .note-block { color: #475569; max-width: 600px; }
    .note-block p { margin: 3px 0; }
    .sign-block { text-align: center; display: flex; flex-direction: column; align-items: center; }
    .sign-block .images { display: flex; align-items: center; gap: 5px; }
    .sign-block .stamp { height: 65px; }
    .sign-block .sign { height: 42px; }
    .sign-block .line { border-top: 1px solid #334155; padding-top: 4px; margin-top: 4px; width: 140px; font-weight: 600; font-size: 10.5px; }
    @media print {
      body { padding: 10px; background: white; }
      .container { border: none; padding: 0; max-width: 100%; }
      .no-print { display: none !important; }
    }
    .print-btn { text-align: center; margin-bottom: 16px; display: flex; gap: 10px; justify-content: center; }
    .print-btn button { padding: 8px 22px; font-size: 13px; background: #2563eb; color: white; border: none; border-radius: 5px; cursor: pointer; font-weight: 600; }
    .print-btn button:hover { background: #1d4ed8; }
    .print-btn button.pdf-btn { background: #dc2626; }
    .print-btn button.pdf-btn:hover { background: #b91c1c; }
  </style>
</head>
<body>
  <div class="print-btn no-print">
    <button onclick="window.print()">Print Outstanding Slip</button>
    <button class="pdf-btn" onclick="downloadPdf()">Download PDF</button>
  </div>
  <div class="container">
    <div class="header">
      <img src="/logo.jpg" alt="Airfield Express" />
      <div class="header-text">
        <h1>AIRFIELD EXPRESS PRIVATE LIMITED</h1>
        <h2>Customer Outstanding</h2>
      </div>
    </div>

    <div class="info-section">
      <div class="left">
        <p><span class="label">Customer Name:</span> <strong>${customerName}</strong></p>
        <p><span class="label">Customer Code:</span> <strong>${customerCode}</strong></p>
        <p><span class="label">GSTIN:</span> ${customerGst}</p>
        <p><span class="label">Address:</span> ${customerAddress}</p>
        <p><span class="label">Contact / Email:</span> ${customerPhone !== '-' ? customerPhone : ''}${customerPhone !== '-' && customerEmail !== '-' ? ' | ' : ''}${customerEmail !== '-' ? customerEmail : (customerPhone === '-' ? '-' : '')}</p>
      </div>
      <div class="right" style="text-align: right;">
        <p><span class="label">As on Date:</span> <strong>${slipDate}</strong></p>
        <p><span class="label">${isFilteredBySelection ? "No of Invoices:" : "Pending Items:"}</span> <strong>${allPendingItems.length}</strong></p>
        <p><span class="label">CIN:</span> U64120DL2012PTC243510</p>
        <p><span class="label">Company GSTIN:</span> ${companyGst}</p>
      </div>
    </div>

    <div class="title">STATEMENT OF OUTSTANDING INVOICES</div>

    ${allPendingItems.length > 0 ? `
    <table>
      <thead>
        <tr>
          <th style="width: 32px;">Sr.</th>
          <th>Invoice No</th>
          <th>Invoice Date</th>
          <th>Billing Period</th>
          <th>Invoice Amt (₹)</th>
          <th>Paid / Recvd (₹)</th>
          <th>TDS (₹)</th>
          <th>Pending Amt (₹)</th>
        </tr>
      </thead>
      <tbody>
        ${tableRowsHtml}
        <tr class="total-row">
          <td colspan="4" class="text-center font-bold">${isFilteredBySelection ? "TOTAL OF SELECTED INVOICES" : "TOTAL OF PENDING INVOICES & NOTES"}</td>
          <td class="text-right">${totalPendingInvAmt.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          <td class="text-right">${totalPendingAllocated.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          <td class="text-right">${totalPendingTds.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          <td class="text-right font-bold">${totalPendingOutstanding.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
      </tbody>
    </table>
    ` : `
    <div style="padding: 20px; text-align: center; background: #f0fdf4; border: 1px solid #86efac; border-radius: 6px; margin: 16px 0; color: #166534; font-size: 13px;">
      <strong>No Pending Invoices!</strong> All invoices are cleared.
    </div>
    `}

    ${creditNoteItems.length > 0 ? `
    <div style="margin-top: 14px; margin-bottom: 6px; font-weight: bold; font-size: 11.5px; color: #0f172a;">
      CREDIT NOTE(S) ISSUED (AVAILABLE CREDIT):
    </div>
    <table>
      <thead>
        <tr>
          <th style="width: 32px;">Sr.</th>
          <th>Credit Note No</th>
          <th>Note Date</th>
          <th>Reason / Remarks</th>
          <th>Credit Amt (₹)</th>
        </tr>
      </thead>
      <tbody>
        ${creditNoteItems.map((cn: any, idx: number) => `
          <tr>
            <td class="text-center">${idx + 1}</td>
            <td class="text-center font-bold" style="color: #1d4ed8;">${cn.invoiceNo}</td>
            <td class="text-center">${moment(cn.date).format("DD/MM/YYYY")}</td>
            <td class="text-center">${cn.remark || "-"}</td>
            <td class="text-right font-bold" style="color: #1d4ed8;">${Number(cn.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
          </tr>
        `).join("")}
        <tr class="total-row">
          <td colspan="4" class="text-center font-bold">TOTAL CREDIT NOTES</td>
          <td class="text-right font-bold" style="color: #1d4ed8;">${totalCnAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
      </tbody>
    </table>
    ` : ""}

    <div class="summary-box">
      <table class="summary-table">
        ${summaryRowsHtml}
      </table>
    </div>

    <p class="amount-words"><strong>Amount in Words:</strong> ${words}</p>

    <div class="footer">
      <div class="note-block">
        <p>• This is a system-generated Customer Outstanding Slip${isFilteredBySelection ? " for selected invoices" : ""}.</p>
        <p>• Kindly verify the outstanding details and arrange the pending payment.</p>
        <p>• For accounts reconciliation, email: <strong>accounts@airfield.co.in</strong> | Contact: <strong>+91 9227223131</strong></p>
      </div>
      <div class="sign-block">
        <div class="images">
          <img class="stamp" src="/images/stamp1.png" alt="Company Stamp" />
          <img class="sign" src="/images/sanjiv_sign.png" alt="Signature" />
        </div>
        <div class="line">Authorized Signatory</div>
      </div>
    </div>
  </div>

  <script>
    function downloadPdf() {
      var el = document.querySelector('.container');
      var opt = {
        margin: [8, 8, 8, 8],
        filename: 'Outstanding_Slip_${(customerName || "Customer").replace(/[^a-zA-Z0-9]/g, '_')}_${isFilteredBySelection ? "Selected_" : ""}${moment().format("DDMMYYYY")}.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      html2pdf().set(opt).from(el).save();
    }
  </script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js"></script>
</body>
</html>
    `;

    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.write(html);
      newWindow.document.close();
    }
  };

  useImperativeHandle(ref, () => ({
    handleOutstandingSlip,
  }));

  const selectableInvoices = (tableData || []).filter((r: any) => r.type === "DEBIT" && r.invoiceId && !r.isDebitNote);
  const allSelectableIds = selectableInvoices.map((r: any) => r.invoiceId);
  const isAllSelected = allSelectableIds.length > 0 && allSelectableIds.every((id: any) => selectedInvoice?.includes(id));

  return (
    <>
      {showSum && selectedSum !== null && (
        <div className="absolute top-16 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 font-semibold">
          Sum: {selectedSum.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      )}
      <div>
        <div className="flex justify-between items-center mb-2 px-1">
          <div className="text-sm font-semibold text-gray-700">
            Customer Statement: <span className="text-blue-700">{customerData?.sh || customerData?.name || "Customer"}</span> ({customerData?.code || ""})
          </div>
          <div className="flex items-center gap-2">
            {selectedInvoice && selectedInvoice.length > 0 && (
              <Button
                size="sm"
                variant="flat"
                color="default"
                className="h-8 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-800"
                onPress={() => setSelectedInvoice([])}
              >
                Clear Selection ({selectedInvoice.length})
              </Button>
            )}
            <Button
              size="sm"
              color="primary"
              className="bg-indigo-600 text-white font-medium hover:bg-indigo-700 shadow-sm flex items-center gap-1.5"
              onPress={handleOutstandingSlip}
              startContent={<PrinterIcon className="w-4 h-4" />}
            >
              Outstanding Slip{selectedInvoice && selectedInvoice.length > 0 ? ` (${selectedInvoice.length} Selected)` : ""}
            </Button>
          </div>
        </div>
        <table className="w-full table- mb-4 text-sm" id="myTable" ref={tableRef}>
          <thead>
            <tr>
              <th className="border border-slate-200 ignore-excel px-1 text-center">
                {selectableInvoices.length > 0 ? (
                  <Checkbox
                    size="sm"
                    isSelected={isAllSelected}
                    onValueChange={(isSelected) => {
                      if (isSelected) {
                        const pendingIds = selectableInvoices
                          .filter((r: any) => Number(r.outstanding || 0) >= 10)
                          .map((r: any) => r.invoiceId);
                        setSelectedInvoice(pendingIds.length > 0 ? pendingIds : allSelectableIds);
                      } else {
                        setSelectedInvoice([]);
                      }
                    }}
                    title={isAllSelected ? "Deselect All" : "Select Pending Invoices"}
                  />
                ) : (
                  "-"
                )}
              </th>
              <th className="border border-slate-200">Sr.</th>
              <th className="border border-slate-200 w-[120px]">From-To</th>
              <th className="border border-slate-200">Invoice Date</th>
              <th className="border border-slate-200">
                Pay Recv
                <br />
                Date
              </th>
              <th className="border border-slate-200">Invoice No</th>
              <th className="border border-slate-200">DEBIT</th>
              <th className="border border-slate-200">CREDIT</th>
              <th className="border border-slate-200">Balance</th>
              <th className="border border-slate-200">TDS</th>
              <th className="border border-slate-200">
                Eff.
                <br />
                Rcvd
              </th>
              <th className="border border-slate-200">
                Invoice
                <br />
                Outstanding
              </th>
              <th className="border border-slate-200">Status</th>
              <th className="border border-slate-200">Days</th>
              <th className="border border-slate-200">Remark</th>
              <th className="border border-slate-200">Followup Date</th>
              <th className="border border-slate-200">Added By</th>
              <th className="border border-slate-200">Added On</th>
              <th className="border border-slate-200 ignore-excel">Delete</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((r: any, i: number) => {
              return (
                <tr
                  key={"asdasd" + i}
                  className={`${
                    selectedInvoice && selectedInvoice.includes(r.invoiceId)
                      ? "bg-indigo-100/90 font-medium "
                      : r["status"] === "Cleared"
                      ? "bg-green-300"
                      : r["status"] === "Pending"
                      ? "bg-yellow-100/50"
                      : ""
                  } ${
                    r.type === "CREDIT" ? (r.isCreditNote ? "bg-blue-50/70" : "bg-slate-100") : (r.isDebitNote ? "bg-orange-50/70" : "")
                  }`}
                >
                  <th className="border border-slate-200 px-1 text-center ignore-excel">
                    {r.type == "DEBIT" && r.invoiceId && !r.isDebitNote ? (
                      <Checkbox
                        size="sm"
                        isSelected={selectedInvoice && selectedInvoice.includes(r.invoiceId)}
                        onValueChange={() => {
                          if (selectedInvoice.includes(r.invoiceId)) {
                            setSelectedInvoice(selectedInvoice.filter((id: any) => id !== r.invoiceId));
                          } else {
                            console.log("Adding invoiceId:", selectedInvoice, r.invoiceId);
                            setSelectedInvoice([...selectedInvoice, r.invoiceId]);
                          }
                        }}
                      ></Checkbox>
                    ) : (
                      "-"
                    )}
                  </th>
                  <td className="border border-slate-200 px-2 text-center">{i + 1}</td>
                  <td className="border border-slate-200 px-2 text-center">
                    {r.fromDate && r.toDate ? r.fromDate + " to " + r.toDate : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-center w-[80px]">
                    {r.type == "DEBIT" ? moment(r.date).format("DD-MM-YYYY") : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-center w-[80px]">
                    {r.type == "CREDIT" ? moment(r.date).format("DD-MM-YYYY") : ""}
                  </td>

                  <td className="border border-slate-200 px-2 text-center font-RobotoCondensed">
                    {r.subType === "CN" ? (
                      <span className="inline-flex items-center">
                        <span className="whitespace-nowrap font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded text-xs border border-blue-200">
                          {r.invoiceNo}
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            await navigator.clipboard.writeText(r.invoiceNo || "");
                            UseToast("Copied!");
                          }}
                          title="Copy Credit Note No"
                        >
                          <DocumentDuplicateIcon className="h-5 -mb-1 ml-1 text-slate-400 hover:text-slate-600" />
                        </button>
                      </span>
                    ) : r.subType === "DN" ? (
                      <span className="inline-flex items-center">
                        <span className="whitespace-nowrap font-semibold text-orange-700 bg-orange-100 px-2 py-0.5 rounded text-xs border border-orange-200">
                          {r.invoiceNo}
                        </span>
                        <button
                          type="button"
                          onClick={async () => {
                            await navigator.clipboard.writeText(r.invoiceNo || "");
                            UseToast("Copied!");
                          }}
                          title="Copy Debit Note No"
                        >
                          <DocumentDuplicateIcon className="h-5 -mb-1 ml-1 text-slate-400 hover:text-slate-600" />
                        </button>
                      </span>
                    ) : (
                      <>
                        {r.invoiceNo}
                        {r.type == "CREDIT" && r.payments && r.payments.length > 1 ? (
                          <Popover>
                            {({ open }) => (
                              <>
                                <Popover.Button className={"flex items-center"}>
                                  Show
                                  <ChevronDownIcon className="ui-open:rotate-180 ui-open:transform h-4" />
                                </Popover.Button>
                                <Transition
                                  show={open}
                                  enter="transition duration-100 ease-out"
                                  enterFrom="transform scale-95 opacity-0"
                                  enterTo="transform scale-100 opacity-100"
                                  leave="transition duration-75 ease-out"
                                  leaveFrom="transform scale-100 opacity-100"
                                  leaveTo="transform scale-95 opacity-0"
                                >
                                  <Popover.Panel
                                    className={
                                      "absolute bg-white p-2 shadow-2xl border-2 border-secondary1 rounded-md -translate-x-full -translate-y-full flex flex-col gap-1"
                                    }
                                  >
                                    {r.payments?.map((p: any, idx: number) => {
                                      return <span key={"pay-state-1inv" + idx}>{invoiceWisePayment[p]?.invoiceNo || p}</span>;
                                    })}
                                  </Popover.Panel>
                                </Transition>
                              </>
                            )}
                          </Popover>
                        ) : (
                          r.payments?.map((p: any, idx: number) => {
                            return <span key={"pay-state-2inv" + idx}>{invoiceWisePayment[p]?.invoiceNo || p}</span>;
                          })
                        )}

                        <button
                          type="button"
                          onClick={async () => {
                            await navigator.clipboard.writeText(
                              r.invoiceNo
                                ? r.invoiceNo
                                : r.type == "CREDIT" && r.payments && r.payments.length > 1
                                ? r.payments?.map((p: any) => invoiceWisePayment[p]?.invoiceNo || p).join(", ")
                                : r.payments?.map((p: any) => invoiceWisePayment[p]?.invoiceNo || p).join(", ")
                            );
                            UseToast("Copied!");
                          }}
                        >
                          <DocumentDuplicateIcon className="h-5 -mb-1 ml-2" />
                        </button>
                      </>
                    )}
                  </td>
                  <td className="border border-slate-200 px-2 text-right">
                    {r.type == "DEBIT" ? Number(r.amount || 0)?.toLocaleString("en-IN") : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-right">
                    {r.type == "CREDIT" ? Number(r.amount || 0)?.toLocaleString("en-IN") : ""}
                  </td>
                  <td className={`border border-slate-200 px-2 text-right font-medium ${Number(r.runningBalance || 0) < 0 ? "text-green-700" : "text-gray-900"}`}>
                    {Number(r.runningBalance || 0)?.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="border border-slate-200 px-2 text-right">
                    {r.type == "CREDIT" && Number(r.tds) > 0 ? Number(r.tds || 0)?.toLocaleString("en-IN") : ""}
                  </td>
                  <td className="border border-slate-200 px-2 text-right">
                    {r.type == "DEBIT" ? Number(r.effRcvd || 0)?.toLocaleString("en-IN") : ""}
                  </td>
                  <td className={`border border-slate-200 px-2 text-right ${Number(r.outstanding || 0) < 0 ? "text-green-700 font-semibold" : ""}`}>
                    {r.type == "DEBIT" ? Number(r.outstanding || 0)?.toLocaleString("en-IN") : ""}
                  </td>
                  <td className={`border border-slate-200 px-2 text-center ${r.status === "Overpaid" ? "text-green-700 font-semibold" : ""}`}>
                    {r.status}
                  </td>
                  <td className="border border-slate-200 px-2 text-center">{r.type == "DEBIT" && !r.isDebitNote && moment().diff(moment(r.date), "days")}</td>
                  <td className="border border-slate-200 px-2 text-center"><small>{r.remark}</small></td>
                  <td className="border border-slate-200 px-2 text-center">{r.followupDate}</td>
                  <td className="border border-slate-200 px-1 text-center">{r.addedBy}</td>
                  <td className="border border-slate-200 px-1 text-center w-[80px]">{r.createdAt ? moment(r.createdAt, "DD-MM-YYYY HH:mm:ss").format("DD-MM-YYYY") : "-"}</td>
                  <td className="border border-slate-200 px-2 text-center ignore-excel">
                    {r.type === "CREDIT" && r.paymentId && !r.isCreditNote && (
                      <button
                        type="button"
                        onClick={() => handleDeletePayment(r.paymentId)}
                        className="text-red-500 hover:text-red-700 disabled:opacity-50"
                        disabled={isDeleting}
                        title="Delete Payment"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-inter">
              <td className="border border-slate-200 px-4 text-right font-semibold py-2 bg-purple-200" colSpan={6}>
                Total
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-purple-200">
                {(totalCredit + totalTdsCredit + pendingAmount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-purple-200">
                {totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-purple-200">
                {pendingAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-purple-200">
                {totalTdsCredit.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-purple-200" colSpan={9}></td>
            </tr>

            <tr className="font-inter">
              <td className="border border-slate-200 px-4 text-right font-semibold py-2 bg-blue-200" colSpan={6}>
                Pending Amount
              </td>
              <td className="border border-slate-200 px-2 text-center font-semibold py-2 bg-blue-200" colSpan={3}>
                {pendingAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td className="border border-slate-200 px-2 text-right font-semibold py-2 bg-blue-200" colSpan={10}></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
});

export default PaymentStatement;
